// Messages, at /messages: the signed-in person's OWN inbox, for the project selected in
// the top bar. The one-to-one chats sent to them and the groups they are in, to read
// and to answer in text. It is not moderation: nothing of anyone else's conversations
// can be opened here.
//
// The same conversations as the app, through the same server routes and rules
// (services/messagesApi). The switch is the tick each role already has per project:
// Direct Messages for one-to-one chats, Group Chats for groups. A super admin has both
// in every project, with no role there. A side whose tick the person does not hold is
// simply absent, and with neither the page says so in a line. The browser cannot know
// the ticks; it asks, and the server's refusal is the answer.
//
// Read, reply in text, and start: the New button beside the heading starts a one-to-one
// chat or creates a group, through the app's routes (components/messages/NewChatDialog,
// NewGroupDialog). Each choice is offered only when the server says the person may: New
// chat when it served the chat list (the same checks as starting a chat), New group when
// it answers the group picker's check (the same checks as creating a group;
// useMayCreateGroup). With neither there is no button. Once something is started it
// opens in the pane like any other conversation, and the keyboard goes there. Photos
// open and voice notes play.
//
// Managing a group is here too: the open group's Group info button lists its people,
// and anyone can leave it; a group admin can rename it and add or remove people
// (components/messages/GroupInfoDialog). The admin's controls are drawn from the
// group's own answer, and the server decides each action. Leaving closes the group,
// takes its row out of the list and reads the list again, and the keyboard goes back
// to the list. Making someone an admin, mute, and sending a photo or a voice note stay
// the app's.
//
// Freshness is by asking again while the tab is in view (the list every 30 seconds,
// the open conversation every 8, both when the person comes back to the tab); there is
// no live connection on the web yet. A change made here (a message sent; a group made,
// renamed or left; people added or removed) reads the list again at once. Reading the
// LIST never marks anything read or delivered. Only opening a conversation does, and
// that is the pane's business.
//
// The open conversation is in the address (?c= or ?g=), so reload and Back work. A
// project switch reads the list again and closes the open conversation.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, FolderOpen, MessageSquare, MessageSquareOff, type LucideIcon } from 'lucide-react';
import ConversationList from '../components/messages/ConversationList';
import ConversationPane from '../components/messages/ConversationPane';
import NewChatDialog from '../components/messages/NewChatDialog';
import NewGroupDialog from '../components/messages/NewGroupDialog';
import NewMenu, { type NewChoice } from '../components/messages/NewMenu';
import {
  MESSAGES_PATH, conversationPath, openConversationFrom, tellMessagesRead, type OpenConversation,
} from '../components/messages/messagesPath';
import { addOlderRows, mixedList, refreshSide, type ListSide } from '../components/messages/messageText';
import { useInViewPoll } from '../components/messages/useInViewPoll';
import { useMayCreateGroup } from '../components/messages/useMayCreateGroup';
import { messagesAPI, newClientMsgId, type ChatRow, type GroupRow } from '../services/messagesApi';
import { useAuthStore } from '../stores/authStore';

/**
 * One side of the list (the chats, or the groups) as the server last answered for it.
 * 'off' is its plain refusal: the person's role has no tick for that side in this
 * project, or they are not in the project (and not a super admin). The server does not
 * say which.
 */
type Side<T> =
  | ({ status: 'ready' } & ListSide<T>)
  | { status: 'off' }
  | { status: 'unconfirmed' }
  | { status: 'failed' };

interface Lists {
  chats: Side<ChatRow>;
  groups: Side<GroupRow>;
}

const READ_LIST_AGAIN_EVERY_MS = 30_000;

const chatId = (row: ChatRow): string => row.conversationId;
const groupId = (row: GroupRow): string => row.groupId;
const lastSeq = (row: ChatRow | GroupRow): number => row.lastMessageSeq;

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

/** What a failed list call means. 403 is how the server refuses a sign-in it issued itself: also "not for you". */
const refusedSide = (err: unknown): Side<never> => {
  const status = statusOf(err);
  if (status === 404 || status === 403) return { status: 'off' };
  return status === 401 ? { status: 'unconfirmed' } : { status: 'failed' };
};

const readySide = <T,>(body: { data?: T[]; nextBeforeSeq?: number | null } | undefined): Side<T> => ({
  status: 'ready',
  rows: Array.isArray(body?.data) ? body.data : [],
  next: body?.nextBeforeSeq ?? null,
});

const loaded = <T,>(side: Side<T> | undefined): ListSide<T> | null => (side?.status === 'ready' ? side : null);

type ListAnswer<T> = { data: { data?: T[]; nextBeforeSeq?: number | null } };

/** One side asked for: its rows, or what the refusal means. It never throws. */
const readSide = <T,>(call: () => Promise<ListAnswer<T>>): Promise<Side<T>> =>
  Promise.resolve().then(call).then((res) => readySide<T>(res?.data), refusedSide);

/** A side read again, laid over what was loaded. Null: it was not asked this time. */
const laidOver = <T extends ChatRow | GroupRow>(was: Side<T>, now: Side<T> | null, idOf: (row: T) => string): Side<T> => {
  if (now === null) return was;
  // Refused now: the tick is gone, so its rows go too.
  if (now.status === 'off') return now;
  // A call that failed this once does not empty a list that is on screen.
  if (now.status !== 'ready') return was.status === 'ready' ? was : now;
  return was.status === 'ready' ? { status: 'ready', ...refreshSide(was, now, idOf, lastSeq) } : now;
};

/** The next page of a side, added under what was loaded. Null: there was none to ask for, or it failed. */
const withOlder = <T,>(was: Side<T>, more: Side<T> | null, idOf: (row: T) => string): Side<T> =>
  (was.status === 'ready' && more?.status === 'ready' ? { status: 'ready', ...addOlderRows(was, more, idOf) } : was);

// The newest pages' card: white, rounded, a hairline border and a soft shadow, on the
// soft grey the shell gives a screen made of cards.
const FRAME = 'rounded-2xl border border-gray-200 bg-white shadow-sm';
const PAGE = 'mx-auto w-full max-w-[1352px] pb-2 pt-4 sm:px-2';
const TRY_AGAIN = 'font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline';

/** Anything the page says instead of the two panes: the heading, then one small white card with one line. */
function Said({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className={PAGE}>
      <h1 className="text-xl font-bold tracking-tight text-gray-900">Messages</h1>
      <div className={`mx-auto mt-9 w-full max-w-md ${FRAME} p-8 text-center`}>
        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <p className="mt-4 text-sm text-gray-700">{children}</p>
      </div>
    </div>
  );
}

export default function MessagesPage(): ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // No project yet is null, whatever an older saved sign-in left in its place.
  const projectId = useAuthStore((s) => s.selectedProjectId) || null;
  // The person's own employee code, as the server keeps codes: whose messages are "You".
  const me = useAuthStore((s) => String(s.user?.userCode ?? s.user?.userName ?? '').trim().toLowerCase());

  // The conversation the address names. Anyone can type one: the server decides whether it opens.
  const open = useMemo<OpenConversation | null>(() => openConversationFrom(params), [params]);

  // Kept WITH the project it was read for (as pages/home/useRead keeps a number): the
  // moment the project changes, the old list stops being shown, before the new one
  // has even been asked for, and an answer that arrives late for the old one is dropped.
  const [held, setHeld] = useState<{ projectId: number; lists: Lists } | null>(null);
  const [again, setAgain] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const lists = projectId !== null && held?.projectId === projectId ? held.lists : null;

  // What is on screen now, for the calls that start later and must know which sides to ask.
  const heldNow = useRef(held);
  useEffect(() => { heldNow.current = held; }, [held]);
  const refreshing = useRef(false);
  // Asked for while a read was already on its way (a group just made, a first message
  // just sent): that read may have been asked before the change, so one more follows it.
  const readAgain = useRef(false);
  const refreshNow = useRef<() => void>(() => {});

  // "New group" is offered only on the server's yes, asked once per project (and again
  // by Try again). It is asked beside the first list read, not after it.
  const { may: mayCreateGroup, askAgainIfUnsure } = useMayCreateGroup(projectId, again);

  // The server's answers. Serving the chat list makes the checks starting a chat makes;
  // a group needs Group Chats (the group list) and the server's yes to creating one.
  const mayChat = lists?.chats.status === 'ready';
  const mayGroup = lists?.groups.status === 'ready' && mayCreateGroup;

  // The New pop-up that is open, kept with the project it was opened in. It closes on a
  // project switch, and when the server takes the choice away. `justOpened` is the name
  // the pane shows for what was just started, until the conversation answers for itself.
  const [starting, setStarting] = useState<{ choice: NewChoice; projectId: number; clientGroupId: string | null } | null>(null);
  const [justOpened, setJustOpened] = useState<{ open: OpenConversation; name: string } | null>(null);
  const newButton = useRef<HTMLButtonElement>(null);
  const paneSection = useRef<HTMLElement>(null);
  const listSection = useRef<HTMLElement>(null);
  const dialogOpen = starting !== null && starting.projectId === projectId && (starting.choice === 'chat' ? mayChat : mayGroup);
  useEffect(() => {
    if (starting && !dialogOpen) setStarting(null);
  }, [starting, dialogOpen]);
  // The id that makes "Create group" safe to try again. It is kept for the next New
  // group in the same project until a group has been made with it, so a person who
  // closed the pop-up after a lost answer and starts again gets that group, never a copy.
  const groupTryId = useRef<{ projectId: number; id: string } | null>(null);
  const begin = (choice: NewChoice, inProject: number) => {
    let clientGroupId: string | null = null;
    if (choice === 'group') {
      if (groupTryId.current?.projectId !== inProject) groupTryId.current = { projectId: inProject, id: newClientMsgId() };
      clientGroupId = groupTryId.current.id;
    }
    setStarting({ choice, projectId: inProject, clientGroupId });
  };
  // Where the keyboard goes once a pop-up has closed. Given up (Escape, Close, the page
  // behind): back to the New button. Something opened: to the conversation, which on a
  // narrow screen is all that is left on it (the list and its New button are hidden).
  const hadDialog = useRef(false);
  const openedSomething = useRef(false);
  useEffect(() => {
    if (hadDialog.current && !dialogOpen) {
      if (openedSomething.current) paneSection.current?.focus();
      else newButton.current?.focus();
      openedSomething.current = false;
    }
    hadDialog.current = dialogOpen;
  }, [dialogOpen]);

  // First read for this project. Each side answers for itself: one can be refused or
  // fail while the other loads.
  useEffect(() => {
    if (projectId === null) return undefined;
    let current = true;
    Promise.all([
      readSide<ChatRow>(() => messagesAPI.chats(projectId)),
      readSide<GroupRow>(() => messagesAPI.groups(projectId)),
    ]).then(([chats, groups]) => {
      if (current) setHeld({ projectId, lists: { chats, groups } });
    });
    return () => { current = false; };
  }, [projectId, again]);

  // A project switch closes the open conversation: it belongs to the project left
  // behind. Nothing of it is read or drawn under the new project meanwhile: the new
  // project's list is not here yet, and until it is the page draws no pane.
  const projectBefore = useRef(projectId);
  useEffect(() => {
    if (projectId === null) return;
    const before = projectBefore.current;
    projectBefore.current = projectId;
    if (before !== null && before !== projectId && open) navigate(MESSAGES_PATH, { replace: true });
  }, [projectId, open, navigate]);

  /**
   * Read the first page of each side again and lay it over what is loaded. It READS
   * only: no call here marks anything read or delivered. A side that was refused is
   * not asked again; a side that fails this time keeps what it had.
   */
  const refresh = useCallback(() => {
    const was = heldNow.current;
    if (projectId === null || !was || was.projectId !== projectId) return;
    // One read at a time; one asked for meanwhile is made when this one is answered.
    if (refreshing.current) {
      readAgain.current = true;
      return;
    }
    readAgain.current = false;
    askAgainIfUnsure();
    refreshing.current = true;
    Promise.all([
      was.lists.chats.status !== 'off' ? readSide<ChatRow>(() => messagesAPI.chats(projectId)) : null,
      was.lists.groups.status !== 'off' ? readSide<GroupRow>(() => messagesAPI.groups(projectId)) : null,
    ]).then(([chats, groups]) => {
      refreshing.current = false;
      setHeld((latest) => (!latest || latest.projectId !== projectId ? latest : {
        projectId,
        lists: {
          chats: laidOver(latest.lists.chats, chats, chatId),
          groups: laidOver(latest.lists.groups, groups, groupId),
        },
      }));
      if (readAgain.current) {
        readAgain.current = false;
        refreshNow.current();
      }
    });
  }, [projectId, askAgainIfUnsure]);
  useEffect(() => { refreshNow.current = refresh; }, [refresh]);

  const anyReady = lists !== null && (lists.chats.status === 'ready' || lists.groups.status === 'ready');
  useInViewPoll(refresh, READ_LIST_AGAIN_EVERY_MS, anyReady);

  const loadMore = () => {
    if (projectId === null || lists === null || loadingMore) return;
    const chats = loaded(lists.chats);
    const groups = loaded(lists.groups);
    // The list is shown only down to where both sides are known (see mixedList). The
    // side that stops highest is the one holding it back, so that is the one to ask.
    const floor = Math.max(chats?.next ?? -Infinity, groups?.next ?? -Infinity);
    if (floor === -Infinity) return;
    setLoadingMore(true);
    Promise.all([
      chats && chats.next === floor ? readSide<ChatRow>(() => messagesAPI.chats(projectId, floor)) : null,
      groups && groups.next === floor ? readSide<GroupRow>(() => messagesAPI.groups(projectId, floor)) : null,
    ]).then(([moreChats, moreGroups]) => {
      setLoadingMore(false);
      setHeld((latest) => (!latest || latest.projectId !== projectId ? latest : {
        projectId,
        lists: {
          chats: withOlder(latest.lists.chats, moreChats, chatId),
          groups: withOlder(latest.lists.groups, moreGroups, groupId),
        },
      }));
    });
  };

  /**
   * The person left the open group (Group info). The server has taken them out, so its
   * row goes now; the group closes, the list is read again, and once the list is on
   * screen (on a narrow screen it is hidden while a conversation is open) the keyboard
   * goes there. The top bar's count is old too: the group's unread went with it.
   */
  const backToList = useRef(false);
  const leftGroup = useCallback((gone: string) => {
    setHeld((latest) => {
      if (!latest || latest.lists.groups.status !== 'ready') return latest;
      const groups = latest.lists.groups;
      return { ...latest, lists: { ...latest.lists, groups: { ...groups, rows: groups.rows.filter((r) => r.groupId !== gone) } } };
    });
    backToList.current = true;
    // In place of the group's address, so Back does not lead to a group the person has left.
    navigate(MESSAGES_PATH, { replace: true });
    refresh();
    tellMessagesRead();
  }, [navigate, refresh]);
  useEffect(() => {
    if (open || !backToList.current) return;
    backToList.current = false;
    listSection.current?.focus();
  }, [open]);

  /** The pane read a conversation: lower that row's count, and tell the top bar its count is old. */
  const onRead = useCallback((which: OpenConversation, unreadCount: number) => {
    setHeld((latest) => {
      if (!latest) return latest;
      const { chats, groups } = latest.lists;
      return {
        ...latest,
        lists: {
          chats: which.kind === 'direct' && chats.status === 'ready'
            ? { ...chats, rows: chats.rows.map((r) => (r.conversationId === which.id ? { ...r, unreadCount } : r)) }
            : chats,
          groups: which.kind === 'group' && groups.status === 'ready'
            ? { ...groups, rows: groups.rows.map((r) => (r.groupId === which.id ? { ...r, unreadCount } : r)) }
            : groups,
        },
      };
    });
    tellMessagesRead();
  }, []);

  const rows = useMemo(() => mixedList(loaded(lists?.chats), loaded(lists?.groups)), [lists]);

  if (projectId === null) {
    return <Said icon={FolderOpen}>Select a project to see its messages.</Said>;
  }
  if (lists === null) {
    return (
      <div className={PAGE}>
        <h1 className="text-xl font-bold tracking-tight text-gray-900">Messages</h1>
        <p role="status" className="mt-9 text-center text-sm text-gray-500">Loading…</p>
      </div>
    );
  }
  const retry = () => {
    setHeld(null);
    setAgain((n) => n + 1);
  };
  const statuses = [lists.chats.status, lists.groups.status];
  if (statuses.includes('unconfirmed')) {
    // The server could not name the caller from the sign-in. Said here, in a line: it
    // must never become the Session Expired dialog (every call on this page is quiet).
    return (
      <Said icon={AlertTriangle}>
        Your sign-in could not be confirmed, so messages cannot be shown.{' '}
        <button type="button" onClick={retry} className={TRY_AGAIN}>Try again</button>
      </Said>
    );
  }
  if (!anyReady) {
    if (statuses.includes('failed')) {
      return (
        <Said icon={AlertTriangle}>
          Messages could not be loaded.{' '}
          <button type="button" onClick={retry} className={TRY_AGAIN}>Try again</button>
        </Said>
      );
    }
    return <Said icon={MessageSquareOff}>Messages is not switched on for you in this project.</Said>;
  }

  const hasMore = (loaded(lists.chats)?.next ?? null) !== null || (loaded(lists.groups)?.next ?? null) !== null;
  const named = open && justOpened && justOpened.open.kind === open.kind && justOpened.open.id === open.id ? justOpened.name : null;
  const knownName = open ? rows.find((r) => r.kind === open.kind && r.id === open.id)?.name ?? named : null;

  const closeDialog = () => setStarting(null);
  /** Open what was just started in the pane, through the address like any other conversation. */
  const show = (which: OpenConversation, name: string) => {
    openedSomething.current = true;
    setJustOpened({ open: which, name });
    setStarting(null);
    navigate(conversationPath(which));
  };

  return (
    <div className={PAGE}>
      {/* Two panes on a wide screen. On a narrow one they take turns: the list, then the open conversation. */}
      <div className="grid h-[calc(100vh-11rem)] min-h-[26rem] gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <section
          ref={listSection}
          aria-label="Conversations"
          // Focusable from code only: the keyboard lands here when the person has left a group.
          tabIndex={-1}
          className={`${FRAME} min-h-0 flex-col overflow-hidden focus:outline-none ${open ? 'hidden lg:flex' : 'flex'}`}
        >
          <ConversationList
            rows={rows}
            me={me}
            open={open}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            partFailed={statuses.includes('failed')}
            onRetry={retry}
            action={(
              <NewMenu
                chat={mayChat}
                group={mayGroup}
                onPick={(choice) => begin(choice, projectId)}
                buttonRef={newButton}
              />
            )}
          />
        </section>
        <section
          ref={paneSection}
          aria-label="Open conversation"
          // Focusable from code only: the keyboard lands here when a chat or group has just been started.
          tabIndex={-1}
          className={`${FRAME} min-h-0 flex-col overflow-hidden focus:outline-none ${open ? 'flex' : 'hidden lg:flex'}`}
        >
          {open ? (
            <ConversationPane
              // A new pane for each conversation and each project: nothing carries over.
              key={`${projectId}:${open.kind}:${open.id}`}
              open={open}
              projectId={projectId}
              me={me}
              knownName={knownName}
              onRead={onRead}
              onSent={refresh}
              onGroupChanged={refresh}
              onLeftGroup={leftGroup}
              takeFocus={named !== null}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <MessageSquare className="h-8 w-8 text-gray-300" aria-hidden="true" />
              <p className="text-sm text-gray-500">Pick a conversation to read it.</p>
            </div>
          )}
        </section>
      </div>

      {dialogOpen && starting.choice === 'chat' && (
        <NewChatDialog
          projectId={projectId}
          onClose={closeDialog}
          // An empty chat is not in the list until its first message, which reads the list again.
          onOpened={(id, person) => show({ kind: 'direct', id }, person.name)}
        />
      )}
      {dialogOpen && starting.choice === 'group' && starting.clientGroupId !== null && (
        <NewGroupDialog
          projectId={projectId}
          clientGroupId={starting.clientGroupId}
          onClose={closeDialog}
          onCreated={(id, title) => {
            // Made: the next New group is a new group, with a new id.
            groupTryId.current = null;
            show({ kind: 'group', id }, title);
            // A new group has its first line ("… created the group"), so it is in the list now.
            refresh();
          }}
        />
      )}
    </div>
  );
}
